/**
 * Shared alert primitives used across the app for any "something went wrong"
 * or "you're good!" UI. Replaces the ad-hoc `bg-red-100 border-red-300`
 * pills that were scattered through the auth modals, support / feedback /
 * bug-report modals, and the profile / saved / chat toast banners.
 *
 * Two flavours:
 *
 *   <InlineAlert variant="error|success|warning|info" compact>
 *     Your message here.
 *   </InlineAlert>
 *
 *     Inline, modal-friendly. Use inside forms or modal bodies. Pass
 *     `compact` for slightly smaller padding + xs text (sign-up form fields).
 *
 *   <FloatingToast variant="error|success" message="..." visible={true} />
 *
 *     Top-centered floating toast. Used by SavedPage / ChatSearchResults /
 *     ProfilePage to show transient success / error messages after a save
 *     or share. Self-contained — caller controls visibility with a
 *     setTimeout pair already.
 *
 * Palette + icon family chosen to match the Banner component
 * (landing-page/components/Banner.tsx). Soft 50/200/700 ramps for low
 * visual weight + good contrast on white modal bodies.
 */

import React from 'react';

export type AlertVariant = 'error' | 'success' | 'warning' | 'info';

interface VariantStyle {
  bg: string;
  border: string;
  iconColor: string;
  textColor: string;
}

const VARIANT_STYLES: Record<AlertVariant, VariantStyle> = {
  error:   { bg: 'bg-red-50',    border: 'border-red-200',    iconColor: 'text-red-600',   textColor: 'text-red-700' },
  success: { bg: 'bg-green-50',  border: 'border-green-200',  iconColor: 'text-green-600', textColor: 'text-green-700' },
  warning: { bg: 'bg-amber-50',  border: 'border-amber-200',  iconColor: 'text-amber-600', textColor: 'text-amber-800' },
  info:    { bg: 'bg-blue-50',   border: 'border-blue-200',   iconColor: 'text-blue-600',  textColor: 'text-blue-800' },
};

const ErrorGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M12 8v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SuccessGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M8 12.5l2.5 2.5L16 9.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WarningGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3l10 18H2L12 3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M12 10v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M12 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const InfoGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M12 11v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const Glyph: React.FC<{ variant: AlertVariant; className?: string }> = ({ variant, className }) => {
  switch (variant) {
    case 'error':   return <ErrorGlyph className={className} />;
    case 'success': return <SuccessGlyph className={className} />;
    case 'warning': return <WarningGlyph className={className} />;
    case 'info':    return <InfoGlyph className={className} />;
  }
};

// ---------------------------------------------------------------------------
// InlineAlert — for use inside modals / forms
// ---------------------------------------------------------------------------

export interface InlineAlertProps {
  variant?: AlertVariant;
  children: React.ReactNode;
  /** Compact mode: smaller padding + xs text. Default false. */
  compact?: boolean;
  /** Extra Tailwind classes on the outer container (margin, width, etc.). */
  className?: string;
  /** Override the auto-selected ARIA role (alert for error/warning, status otherwise). */
  role?: 'alert' | 'status';
}

export const InlineAlert: React.FC<InlineAlertProps> = ({
  variant = 'error',
  children,
  compact = false,
  className = '',
  role,
}) => {
  const palette = VARIANT_STYLES[variant];
  const ariaRole = role ?? (variant === 'error' || variant === 'warning' ? 'alert' : 'status');
  const pad = compact ? 'p-2.5' : 'p-3';
  const txt = compact ? 'text-xs' : 'text-sm';

  return (
    <div
      role={ariaRole}
      className={`w-full flex items-start gap-2.5 ${pad} rounded-xl border ${palette.bg} ${palette.border} ${className}`}
    >
      <Glyph variant={variant} className={`w-4 h-4 mt-0.5 shrink-0 ${palette.iconColor}`} />
      <div className={`${txt} ${palette.textColor} leading-snug text-left flex-1 min-w-0`}>
        {children}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// FloatingToast — fixed top-center toast for transient messages
// ---------------------------------------------------------------------------

export interface FloatingToastProps {
  variant?: Extract<AlertVariant, 'error' | 'success'>;
  message: React.ReactNode;
  /** When false, nothing is rendered. */
  visible?: boolean;
}

export const FloatingToast: React.FC<FloatingToastProps> = ({
  variant = 'success',
  message,
  visible = true,
}) => {
  if (!visible) return null;
  const palette = VARIANT_STYLES[variant];

  return (
    <div className="fixed inset-x-0 top-0 z-50 flex justify-center pt-4 sm:pt-6 pointer-events-none px-4">
      <div
        role={variant === 'error' ? 'alert' : 'status'}
        className={`pointer-events-auto flex items-start gap-2.5 px-4 py-3 rounded-xl border shadow-lg max-w-md ${palette.bg} ${palette.border}`}
      >
        <Glyph variant={variant} className={`w-5 h-5 mt-0.5 shrink-0 ${palette.iconColor}`} />
        <div className={`text-sm ${palette.textColor} leading-snug`}>{message}</div>
      </div>
    </div>
  );
};
